/**
 * Sticky bar that appears after scrolling past a threshold (generic block content).
 */

function anchorTargetIdFromHref(href: string): string | null {
  const trimmed = href.trim()
  if (!trimmed) return null
  try {
    const resolved = new URL(trimmed, window.location.href)
    const { hash } = resolved
    if (!hash || hash === '#') return null
    const id = decodeURIComponent(hash.slice(1))
    if (!id) return null
    if (resolved.origin !== window.location.origin) return null
    if (resolved.pathname !== window.location.pathname) return null
    return id
  } catch {
    return null
  }
}

export class StickyBar extends HTMLElement {
  static TRANSITION = 'transform 0.3s ease'
  static UI_VISIBLE_SELECTOR = '[data-ui~="visible"]'
  static get UI_THRESHOLD() {
    const sectionPrimary = document.querySelector('main>section:first-child')
    return sectionPrimary ? sectionPrimary.getBoundingClientRect().height / 4 * 3 : window.innerHeight * 1.3
  }

  lastScrollY: number
  ticking: boolean
  isVisible: boolean
  isScrolled: boolean
  bottomOffset: number
  elementHeight: number
  innerElement: HTMLElement | null
  ctaSectionObserver: IntersectionObserver | null
  ctaAnchorsObserved: HTMLAnchorElement[]
  ctaAnchorToTarget: Map<HTMLAnchorElement, Element>
  ctaTargetIntersecting: Map<Element, boolean>

  constructor() {
    super()

    this.lastScrollY = window.scrollY
    this.ticking = false
    this.isVisible = false
    this.isScrolled = false
    this.innerElement = null
    this.elementHeight = 0
    this.ctaSectionObserver = null
    this.ctaAnchorsObserved = []
    this.ctaAnchorToTarget = new Map()
    this.ctaTargetIntersecting = new Map()

    this.style.transition = StickyBar.TRANSITION
    this.bottomOffset = parseInt(window.getComputedStyle(this).bottom.replace('px', ''), 10)

    this.handleScroll = this.handleScroll.bind(this)
  }

  get hiddenPosition() {
    return 'translateY(100%)'
  }

  get visiblePosition() {
    return 'translateY(-100%)'
  }

  connectedCallback() {
    this.bottomOffset = parseInt(window.getComputedStyle(this).bottom.replace('px', ''), 10)
    this.innerElement = this.firstElementChild instanceof HTMLElement ? this.firstElementChild : null
    this.elementHeight = this.innerElement
      ? this.innerElement.getBoundingClientRect().height
      : this.getBoundingClientRect().height

    window.addEventListener('scroll', this.handleScroll, { passive: true })

    this.style.transform = this.hiddenPosition
    this.setupCtaSectionVisibility()
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll)
    this.teardownCtaSectionVisibility()
  }

  showBar() {
    if (!this.isVisible) {
      this.style.transform = this.visiblePosition
      this.isVisible = true
      this.setAttribute('data-visible', 'visible')
    }
  }

  hideBar() {
    if (this.isVisible) {
      this.style.transform = this.hiddenPosition
      this.isVisible = false
      this.removeAttribute('data-visible')
    }
  }

  handleScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        const currentScroll = window.scrollY

        if (currentScroll > StickyBar.UI_THRESHOLD) {
          this.showBar()
        } else {
          this.hideBar()
        }
        this.lastScrollY = currentScroll
        this.ticking = false
      })

      this.ticking = true
    }
  }

  setupCtaSectionVisibility() {
    this.teardownCtaSectionVisibility()

    this.ctaAnchorToTarget.clear()
    this.ctaTargetIntersecting.clear()

    const ctas = this.querySelectorAll<HTMLAnchorElement>('[data-sticky-bar-cta][href]')
    const observedTargets = new Set<Element>()

    ctas.forEach((cta) => {
      const id = anchorTargetIdFromHref(cta.getAttribute('href') || '')
      if (!id) return
      const target = document.getElementById(id)
      if (!target) return
      this.ctaAnchorToTarget.set(cta, target)
      observedTargets.add(target)
    })

    if (observedTargets.size === 0) return

    this.ctaAnchorsObserved = [...this.ctaAnchorToTarget.keys()]

    this.ctaSectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        this.ctaTargetIntersecting.set(entry.target, entry.isIntersecting)
      }
      this.applyExclusiveCtaSectionVisibility()
    })

    observedTargets.forEach((el) => this.ctaSectionObserver!.observe(el))
  }

  /**
   * When multiple anchored sections are in view, only the last CTA in bar DOM order gets data-ui.
   */
  applyExclusiveCtaSectionVisibility() {
    const ctasInBarOrder = this.querySelectorAll<HTMLAnchorElement>('[data-sticky-bar-cta][href]')
    let lastInViewInBarOrder: HTMLAnchorElement | null = null
    for (const cta of ctasInBarOrder) {
      const target = this.ctaAnchorToTarget.get(cta)
      if (!target) continue
      if (this.ctaTargetIntersecting.get(target) === true) {
        lastInViewInBarOrder = cta
      }
    }
    for (const cta of this.ctaAnchorsObserved) {
      cta.removeAttribute('data-ui')
    }
    if (lastInViewInBarOrder) {
      lastInViewInBarOrder.setAttribute('data-ui', 'visible')
    }
  }

  teardownCtaSectionVisibility() {
    this.ctaSectionObserver?.disconnect()
    this.ctaSectionObserver = null
    for (const cta of this.ctaAnchorsObserved) {
      cta.removeAttribute('data-ui')
    }
    this.ctaAnchorsObserved = []
    this.ctaAnchorToTarget.clear()
    this.ctaTargetIntersecting.clear()
  }
}
